/**
 * The single-input confirmation dialog, extracted from `GateDetailScreen`'s inline copy so the
 * drawer's self-name editor (D133) is not a second hand-rolled one.
 *
 * It renders against the `.popover*` rules already in `global.css`. Note that
 * `.popover-backdrop` sits above `.drawer` (z-index 42 vs 35) — a popover is always the topmost
 * thing on screen when it exists, and the drawer is what forced that ordering. Render it as a
 * *sibling* of `<Drawer>`, never a child: a child would inherit the drawer's stacking context and
 * the z-index would not help.
 */
import type { ReactNode } from 'react'
import { PrimaryButton, SecondaryButton } from './Button'

export function Popover({
  title,
  body,
  value,
  onChange,
  error,
  busy = false,
  cancelLabel,
  confirmLabel,
  onCancel,
  onConfirm,
}: {
  readonly title: string
  readonly body?: ReactNode
  readonly value: string
  onChange(next: string): void
  /** The message to show under the input; its presence is what marks the input invalid. */
  readonly error?: string
  readonly busy?: boolean
  readonly cancelLabel: string
  readonly confirmLabel: string
  onCancel(): void
  onConfirm(): void
}): JSX.Element {
  return (
    <div className="popover-backdrop" onClick={onCancel}>
      <div className="popover" role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
        <h2 className="popover-title">{title}</h2>
        {body === undefined ? null : <p className="popover-body">{body}</p>}
        <input
          className="input"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          autoComplete="off"
          spellCheck={false}
          disabled={busy}
          aria-invalid={error !== undefined}
        />
        {error === undefined ? null : <p className="popover-error">{error}</p>}
        <div className="popover-actions">
          <SecondaryButton onClick={onCancel} disabled={busy}>
            {cancelLabel}
          </SecondaryButton>
          <PrimaryButton onClick={onConfirm} disabled={busy}>
            {confirmLabel}
          </PrimaryButton>
        </div>
      </div>
    </div>
  )
}
